/**
 * Customer giriş dialog — Google ile Giriş + e-posta OTP (parolasız).
 *
 * - Google butonu: VITE_GOOGLE_CLIENT_ID varsa görünür, tek tık giriş.
 * - E-posta OTP: 1. adım sadece e-posta. Kod adımında backend is_new=true
 *   ise (yeni müşteri) ad-soyad + opsiyonel telefon istenir; mevcut
 *   müşteriden istenmez (sürtünme azalt).
 */
import { Button, Modal, PinInput, Stack, Text, TextInput, Alert, Group, Divider, Center } from "@mantine/core";
import { IconMail, IconUser, IconPhone, IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { apiClient } from "../../shared/api";
import { validateEmail } from "../../shared/ui/validators";

const GOOGLE_ENABLED = !!(import.meta.env["VITE_GOOGLE_CLIENT_ID"] || "");

interface Props {
  opened: boolean;
  onClose: () => void;
  qrToken: string;
  onLoginSuccess: (data: { customer_token: string; customer_id: string; display_name: string; phone: string }) => void;
}

export function CustomerLoginModal({ opened, onClose, qrToken, onLoginSuccess }: Props) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Dev kolaylığı: Resend bağlı değilken backend DEBUG modda kodu döner.
  const [devCode, setDevCode] = useState<string | null>(null);

  function reset() {
    setStep("email"); setEmail(""); setIsNew(false); setDisplayName("");
    setPhone(""); setCode(""); setDevCode(null); setError(null);
  }

  function finishLogin(data: any) {
    localStorage.setItem("customer_token", data.customer_token);
    localStorage.setItem("customer_id", data.customer_id);
    onLoginSuccess(data);
    onClose();
    reset();
  }

  async function requestOtp() {
    setError(null); setDevCode(null);
    const emailErr = validateEmail(email);
    if (emailErr) { setError(emailErr); return; }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/qr/otp/request/", { qr_token: qrToken, email });
      setIsNew(!!data?.is_new);   // yeni müşteri → ad/tel iste
      setStep("code");
      if (data?.dev_code) { setDevCode(data.dev_code); setCode(data.dev_code); }
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Kod gönderilemedi");
    } finally { setLoading(false); }
  }

  async function verifyOtp() {
    setError(null);
    // Yeni müşteri kaydında ad-soyad zorunlu (telefon opsiyonel).
    if (isNew && displayName.trim().length < 2) {
      setError("Ad Soyad zorunludur.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/qr/otp/verify/", {
        qr_token: qrToken, email, code,
        // Ad/telefon yalnız yeni müşteride gönderilir.
        display_name: isNew ? displayName.trim() : "",
        phone: isNew ? phone.trim() : "",
      });
      finishLogin(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Kod hatalı");
    } finally { setLoading(false); }
  }

  async function googleLogin(idToken: string) {
    setError(null); setLoading(true);
    try {
      const { data } = await apiClient.post("/qr/oauth/google/", {
        qr_token: qrToken, id_token: idToken,
      });
      finishLogin(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Google girişi başarısız");
    } finally { setLoading(false); }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Hesabına Giriş" size="sm">
      <Stack gap="md">
        {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>{error}</Alert>}

        {step === "email" && (
          <>
            {GOOGLE_ENABLED && (
              <>
                <Center>
                  <GoogleLogin
                    onSuccess={(cred) => {
                      if (cred.credential) void googleLogin(cred.credential);
                    }}
                    onError={() => setError("Google girişi başarısız")}
                    text="continue_with"
                    shape="pill"
                  />
                </Center>
                <Divider label="veya e-posta ile" labelPosition="center" />
              </>
            )}
            <Text size="sm" c="dimmed">
              E-postanıza 6 haneli kod gönderilecek. Loyalty puanlarınızı ve geçmiş siparişlerinizi görmek için giriş yapın.
            </Text>
            <TextInput
              label="E-posta"
              placeholder="ornek@mail.com"
              type="email"
              leftSection={<IconMail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              size="lg"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && requestOtp()}
            />
            <Button onClick={requestOtp} loading={loading} disabled={!email} fullWidth size="lg">
              Kod Gönder
            </Button>
          </>
        )}

        {step === "code" && (
          <>
            <Text size="sm" c="dimmed">
              {email} adresine gelen 6 haneli kodu girin
            </Text>
            {devCode && (
              <Alert color="blue" icon={<IconAlertCircle size={16} />}>
                Test modu (e-posta sağlayıcı bağlı değil) — kod: <b>{devCode}</b> (otomatik dolduruldu)
              </Alert>
            )}
            <Group justify="center">
              <PinInput length={6} value={code} onChange={setCode} size="lg" type="number" />
            </Group>
            {/* Ad/telefon yalnız YENİ müşteride sorulur */}
            {isNew && (
              <>
                <TextInput
                  label="Ad Soyad"
                  required
                  placeholder="Adınız Soyadınız"
                  leftSection={<IconUser size={16} />}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.currentTarget.value)}
                  size="md"
                />
                <TextInput
                  label="Telefon (opsiyonel)"
                  placeholder="+90 5XX XXX XX XX"
                  leftSection={<IconPhone size={16} />}
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                  size="md"
                />
              </>
            )}
            <Button
              onClick={verifyOtp}
              loading={loading}
              disabled={code.length !== 6 || (isNew && displayName.trim().length < 2)}
              fullWidth size="lg"
              leftSection={<IconCheck size={18} />}
            >
              Doğrula ve Giriş Yap
            </Button>
            <Button variant="subtle" onClick={() => setStep("email")}>← E-postayı Değiştir</Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}
